import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { listPages, getPage, savePage, deletePage } from '../../services/pages';
import MediaPicker from './components/MediaPicker';

const emptyBlock = (type, order) => ({ id: crypto.randomUUID(), type, order, data: {} });

const BlockEditor = ({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const [openPicker, setOpenPicker] = useState(false);
  const update = (key, value) => onChange({ ...block, data: { ...block.data, [key]: value } });

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row justify-between items-center">
        <CardTitle className="text-sm">Bloc: {block.type}</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onMoveUp} disabled={isFirst}>↑</Button>
          <Button variant="outline" onClick={onMoveDown} disabled={isLast}>↓</Button>
          <Button variant="outline" onClick={onRemove}>Supprimer</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {block.type === 'hero' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="Titre" value={block.data.title || ''} onChange={e => update('title', e.target.value)} />
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="Sous-titre" value={block.data.subtitle || ''} onChange={e => update('subtitle', e.target.value)} />
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="CTA Texte" value={block.data.cta_text || ''} onChange={e => update('cta_text', e.target.value)} />
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="CTA Lien" value={block.data.cta_link || ''} onChange={e => update('cta_link', e.target.value)} />
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-2 items-end">
              <div>
                <input className="w-full rounded-md border border-border bg-background px-3 py-2" placeholder="Image URL (optionnel)" value={block.data.image_url || ''} onChange={e => update('image_url', e.target.value)} />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setOpenPicker(true)}>Choisir depuis médiathèque</Button>
              </div>
            </div>
            <MediaPicker open={openPicker} onClose={() => setOpenPicker(false)} onSelect={(url) => update('image_url', url)} mediaType="gallery_image" />
          </div>
        )}
        {block.type === 'text' && (
          <textarea className="w-full min-h-[120px] rounded-md border border-border bg-background px-3 py-2" placeholder="Texte" value={block.data.text || ''} onChange={e => update('text', e.target.value)} />
        )}
        {block.type === 'features' && (
          <div className="space-y-2">
            <small className="text-muted-foreground">Trois features (titre + description)</small>
            {[0,1,2].map(i => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input className="rounded-md border border-border bg-background px-3 py-2" placeholder={`Feature ${i+1} - Titre`} value={(block.data.features?.[i]?.title) || ''} onChange={e => {
                  const features = block.data.features ? [...block.data.features] : [];
                  features[i] = { ...(features[i] || {}), title: e.target.value };
                  update('features', features);
                }} />
                <input className="rounded-md border border-border bg-background px-3 py-2" placeholder={`Feature ${i+1} - Description`} value={(block.data.features?.[i]?.desc) || ''} onChange={e => {
                  const features = block.data.features ? [...block.data.features] : [];
                  features[i] = { ...(features[i] || {}), desc: e.target.value };
                  update('features', features);
                }} />
              </div>
            ))}
          </div>
        )}
        {block.type === 'gallery' && (
          <div className="space-y-2">
            <small className="text-muted-foreground">Ce bloc affiche un lien rapide vers la galerie existante</small>
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="Titre du bloc" value={block.data.title || ''} onChange={e => update('title', e.target.value)} />
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="Texte du bouton" value={block.data.btn || ''} onChange={e => update('btn', e.target.value)} />
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="Lien (par ex. /gallery)" value={block.data.link || ''} onChange={e => update('link', e.target.value)} />
          </div>
        )}
        {block.type === 'actions' && (
          <div className="space-y-3">
            <small className="text-muted-foreground">Cartes d'actions avec icône (icônes disponibles: heart, users, globe, book, shop, image)</small>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input className="rounded-md border border-border bg-background px-3 py-2 md:col-span-2" placeholder="Titre de la section" value={block.data.section_title || ''} onChange={e => update('section_title', e.target.value)} />
              <textarea className="rounded-md border border-border bg-background px-3 py-2 md:col-span-2" placeholder="Sous-titre de la section" value={block.data.section_subtitle || ''} onChange={e => update('section_subtitle', e.target.value)} />
            </div>
            {[0,1,2].map(i => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-2 border-t border-border pt-2">
                <select className="rounded-md border border-border bg-background px-3 py-2" value={(block.data.items?.[i]?.icon) || 'heart'} onChange={e => {
                  const items = block.data.items ? [...block.data.items] : [];
                  items[i] = { ...(items[i] || {}), icon: e.target.value };
                  update('items', items);
                }}>
                  {['heart','users','globe','book','shop','image'].map(ic => <option key={ic} value={ic}>{ic}</option>)}
                </select>
                <input className="rounded-md border border-border bg-background px-3 py-2" placeholder={`Carte ${i+1} - Titre`} value={(block.data.items?.[i]?.title) || ''} onChange={e => {
                  const items = block.data.items ? [...block.data.items] : [];
                  items[i] = { ...(items[i] || {}), title: e.target.value };
                  update('items', items);
                }} />
                <input className="rounded-md border border-border bg-background px-3 py-2" placeholder={`Carte ${i+1} - Description`} value={(block.data.items?.[i]?.desc) || ''} onChange={e => {
                  const items = block.data.items ? [...block.data.items] : [];
                  items[i] = { ...(items[i] || {}), desc: e.target.value };
                  update('items', items);
                }} />
              </div>
            ))}
          </div>
        )}
        {block.type === 'stats' && (
          <div className="space-y-2">
            <small className="text-muted-foreground">Chiffres clés (ex: 12 000 / Bénéficiaires)</small>
            {[0,1,2,3].map(i => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input className="rounded-md border border-border bg-background px-3 py-2" placeholder={`Stat ${i+1} - Valeur (ex: 12k+)`} value={(block.data.stats?.[i]?.value) || ''} onChange={e => {
                  const stats = block.data.stats ? [...block.data.stats] : [];
                  stats[i] = { ...(stats[i] || {}), value: e.target.value };
                  update('stats', stats);
                }} />
                <input className="rounded-md border border-border bg-background px-3 py-2" placeholder={`Stat ${i+1} - Libellé`} value={(block.data.stats?.[i]?.label) || ''} onChange={e => {
                  const stats = block.data.stats ? [...block.data.stats] : [];
                  stats[i] = { ...(stats[i] || {}), label: e.target.value };
                  update('stats', stats);
                }} />
              </div>
            ))}
          </div>
        )}
        {block.type === 'cta' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input className="rounded-md border border-border bg-background px-3 py-2 md:col-span-2" placeholder="Titre" value={block.data.title || ''} onChange={e => update('title', e.target.value)} />
            <textarea className="rounded-md border border-border bg-background px-3 py-2 md:col-span-2" placeholder="Texte" value={block.data.text || ''} onChange={e => update('text', e.target.value)} />
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="Bouton 1 - Texte" value={block.data.btn1_text || ''} onChange={e => update('btn1_text', e.target.value)} />
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="Bouton 1 - Lien" value={block.data.btn1_link || ''} onChange={e => update('btn1_link', e.target.value)} />
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="Bouton 2 - Texte" value={block.data.btn2_text || ''} onChange={e => update('btn2_text', e.target.value)} />
            <input className="rounded-md border border-border bg-background px-3 py-2" placeholder="Bouton 2 - Lien" value={block.data.btn2_link || ''} onChange={e => update('btn2_link', e.target.value)} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BlockPreview = ({ block }) => {
  if (block.type === 'hero') {
    return (
      <div className="p-6 border rounded-lg bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10">
        {block.data.image_url && (
          <img src={block.data.image_url} alt="hero" className="w-full h-48 object-cover rounded mb-3" />
        )}
        <h3 className="text-2xl font-bold mb-2">{block.data.title || 'Titre Hero'}</h3>
        <p className="text-muted-foreground mb-4">{block.data.subtitle || 'Sous-titre'}</p>
        {block.data.cta_text && <a className="inline-block bg-primary text-white px-4 py-2 rounded" href={block.data.cta_link || '#'}>{block.data.cta_text}</a>}
      </div>
    );
  }
  if (block.type === 'text') {
    return (
      <div className="p-4 border rounded-lg"><p className="whitespace-pre-wrap">{block.data.text || 'Texte...'}</p></div>
    );
  }
  if (block.type === 'features') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(block.data.features || [{},{},{}]).map((f, idx) => (
          <div key={idx} className="p-4 border rounded-lg">
            <div className="font-semibold mb-1">{f.title || `Feature ${idx+1}`}</div>
            <div className="text-sm text-muted-foreground">{f.desc || 'Description'}</div>
          </div>
        ))}
      </div>
    );
  }
  if (block.type === 'gallery') {
    return (
      <div className="p-4 border rounded-lg">
        <div className="font-semibold mb-2">{block.data.title || 'Galerie'}</div>
        <a className="text-primary underline" href={block.data.link || '/gallery'}>{block.data.btn || 'Voir la galerie →'}</a>
      </div>
    );
  }
  if (block.type === 'actions') {
    return (
      <div className="p-4 border rounded-lg">
        <div className="font-semibold mb-2">{block.data.section_title || 'Section'}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(block.data.items || [{},{},{}]).map((it, idx) => (
            <div key={idx} className="p-3 border rounded">
              <div className="text-xs text-muted-foreground mb-1">Icône: {it.icon || 'heart'}</div>
              <div className="font-semibold">{it.title || `Carte ${idx+1}`}</div>
              <div className="text-sm text-muted-foreground">{it.desc || 'Description'}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (block.type === 'stats') {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(block.data.stats || [{},{},{},{}]).map((s, idx) => (
          <div key={idx} className="p-4 border rounded-lg text-center">
            <div className="text-2xl font-bold">{s.value || '0'}</div>
            <div className="text-xs text-muted-foreground">{s.label || 'Statistique'}</div>
          </div>
        ))}
      </div>
    );
  }
  if (block.type === 'cta') {
    return (
      <div className="p-6 border rounded-lg text-center bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10">
        <div className="text-xl font-bold mb-2">{block.data.title || 'Titre CTA'}</div>
        <div className="text-sm text-muted-foreground mb-3">{block.data.text || 'Texte...'}</div>
        <div className="flex gap-2 justify-center">
          {block.data.btn1_text && <span className="px-3 py-1.5 rounded bg-primary text-white text-sm">{block.data.btn1_text}</span>}
          {block.data.btn2_text && <span className="px-3 py-1.5 rounded border text-sm">{block.data.btn2_text}</span>}
        </div>
      </div>
    );
  }
  return null;
};

const PageBuilder = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pages, setPages] = useState([]);
  const [slug, setSlug] = useState('home');
  const [title, setTitle] = useState('Page');
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const res = await listPages();
        setPages(res.pages || []);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  if (!user || user.user_type !== 'fondateur') {
    return <div className="p-6 text-sm text-red-500">Accès réservé au fondateur.</div>;
  }

  const loadPage = async () => {
    try {
      setError(''); setSuccess('');
      const res = await getPage(slug);
      const page = res.page;
      if (page) {
        setTitle(page.title || '');
        setBlocks((page.blocks || []).sort((a,b) => (a.order ?? 0) - (b.order ?? 0)));
      } else {
        setTitle('');
        setBlocks([]);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const addBlock = (type) => setBlocks(prev => [...prev, emptyBlock(type, prev.length)]);
  const updateBlock = (idx, newBlock) => setBlocks(prev => prev.map((b,i) => i === idx ? newBlock : b));
  const removeBlock = (idx) => setBlocks(prev => prev.filter((_, i) => i !== idx).map((b, i2) => ({ ...b, order: i2 })));
  const moveBlock = (idx, dir) => { const t = idx + dir; if (t<0 || t>=blocks.length) return; const c=[...blocks]; [c[idx], c[t]]=[c[t], c[idx]]; setBlocks(c.map((b,i)=>({...b, order:i}))); };

  const onSave = async () => {
    try {
      setError(''); setSuccess('');
      const payload = { slug, title, blocks: blocks.map((b,i) => ({ id: b.id, type: b.type, order: i, data: b.data })) };
      await savePage(payload, token);
      setSuccess('✅ Page enregistrée');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const onDelete = async () => {
    if (!slug) return; if (!confirm('Supprimer cette page ?')) return;
    try { setError(''); setSuccess(''); await deletePage(slug, token); setSuccess('✅ Page supprimée'); setTitle(''); setBlocks([]); } catch(e){ setError(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle>Page Builder</CardTitle>
          <CardDescription>Créez et arrangez les blocs. Ajoutez une image Hero depuis la médiathèque.</CardDescription>
        </CardHeader>
        <CardContent>
          {error && <Alert className="mb-4"><AlertDescription className="text-red-600">{error}</AlertDescription></Alert>}
          {success && <Alert className="mb-4"><AlertDescription className="text-green-600">{success}</AlertDescription></Alert>}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm mb-1">Slug</label>
              <input className="w-full rounded-md border border-border bg-background px-3 py-2" value={slug} onChange={e => setSlug(e.target.value.replace(/\s+/g,'-').toLowerCase())} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm mb-1">Titre de la page</label>
              <input className="w-full rounded-md border border-border bg-background px-3 py-2" value={title} onChange={e => setTitle(e.target.value)} />
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <Button variant="outline" onClick={loadPage}>Charger</Button>
            <Button onClick={onSave}>Sauvegarder</Button>
            <Button variant="outline" onClick={onDelete}>Supprimer</Button>
          </div>

          <div className="flex gap-2 mb-4">
            <Button variant="outline" onClick={() => addBlock('hero')}>+ Hero</Button>
            <Button variant="outline" onClick={() => addBlock('text')}>+ Texte</Button>
            <Button variant="outline" onClick={() => addBlock('features')}>+ Features</Button>
            <Button variant="outline" onClick={() => addBlock('gallery')}>+ Galerie</Button>
            <Button variant="outline" onClick={() => addBlock('actions')}>+ Cartes d'actions</Button>
            <Button variant="outline" onClick={() => addBlock('stats')}>+ Chiffres clés</Button>
            <Button variant="outline" onClick={() => addBlock('cta')}>+ Appel à l'action</Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              {blocks.map((b, idx) => (
                <BlockEditor
                  key={b.id}
                  block={b}
                  onChange={(nb) => updateBlock(idx, nb)}
                  onRemove={() => removeBlock(idx)}
                  onMoveUp={() => moveBlock(idx, -1)}
                  onMoveDown={() => moveBlock(idx, 1)}
                  isFirst={idx === 0}
                  isLast={idx === blocks.length - 1}
                />
              ))}
            </div>
            <div>
              <div className="p-4 border rounded-lg">
                <div className="text-sm text-muted-foreground mb-2">Aperçu</div>
                <div className="space-y-4">
                  {blocks.map((b) => (
                    <BlockPreview key={b.id} block={b} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PageBuilder;